import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { handleUnexpectedError } from '@/app/errors';
import { AppFeatures, isFeatureEnabled } from '@/app/features/use-feature';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { useAppForm } from '@/app/lib/tanstack-form';
import { routeMetadata } from '@/app/lib/tanstack-router';
import { Membership } from '@/app/network/models/v1/membership';
import { NotFoundComponent } from '@/app/routes/__root';
import { Button, ButtonGroup, ButtonGroupSpacer } from '@/common/components/button';
import { CheckboxField, CheckboxGroup } from '@/common/components/checkbox';
import { Description, Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import {
  Setting,
  SettingContent,
  SettingName,
  Settings,
  SettingsSection,
  SettingsSet
} from '@/settings/components/settings';
import { SettingsSidebar } from '@/settings/components/settings-sidebar';
import {
  membershipQueryKeys,
  useMembershipPreferences,
  useUpdateMembershipPreferences
} from '@/settings/queries';
import { ContentSection } from '@/shared/components/content-section';
import { MembershipPicker } from '@/shared/components/membership-picker';
import { SectionHeader } from '@/shared/components/sections';
import { Toast } from '@/shared/components/toast';
import { getMembershipLabel } from '@/shared/memberships';

export const Route = createFileRoute('/settings/_layout/membership-preferences/')({
  loader: () => {
    if (!isFeatureEnabled(AppFeatures.MembershipPreferences)) throw notFound();
  },
  head: () => ({
    meta: routeMetadata('Membership Preferences')
  }),
  notFoundComponent: NotFoundComponent,
  component: MembershipPreferencesPage
});

function MembershipPreferencesPage() {
  const { memberships } = useCurrentUser();
  const availableMemberships = memberships.filter(membership => membership.can_grant_to_others);

  const [selectedMembership, setSelectedMembership] = useState<Membership>(availableMemberships[0]);
  const queryClient = useQueryClient();

  const { data: preferences } = useMembershipPreferences(selectedMembership.member_number);

  const updateMembershipPreferencesMutation = useUpdateMembershipPreferences({
    onSuccess: async (_, data) => {
      toast.success(Toast, {
        data: {
          title: 'Preferences Updated',
          content: `The preferences for membership ${getMembershipLabel(data.membership)} have been updated.`
        }
      });

      await queryClient.invalidateQueries({ queryKey: membershipQueryKeys.preferences(data.membership.member_number) });
    },
    onError: error => handleUnexpectedError(error)
  });

  const preferencesForm = useAppForm({
    defaultValues: {
      membership: selectedMembership,
      preferences: {
        paperlessBilling: preferences?.paperlessBilling ?? false,
        paperNewsletterOptOut: preferences?.paperNewsletterOptOut ?? false
      }
    },
    onSubmit: async ({ value }) => {
      await updateMembershipPreferencesMutation.mutateAsync({
        membership: selectedMembership,
        preferences: {
          paperlessBilling: value.preferences.paperlessBilling,
          paperNewsletterOptOut: value.preferences.paperNewsletterOptOut
        }
      });
    }
  });

  const handleSavePreferencesClick = async () => {
    await preferencesForm.handleSubmit();
  };

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <SettingsSidebar active="membership-preferences"/>
      <ContentSection bg="white">
        <SectionHeader collapse>
          <Heading>Membership Preferences</Heading>

          {availableMemberships.length > 1 && (
            <MembershipPicker
              memberships={availableMemberships}
              selectedMembership={selectedMembership}
              onSelectMembership={setSelectedMembership}
            />
          )}
        </SectionHeader>

        <Settings>
          <SettingsSection>
            <SettingsSet>
              <Setting>
                <SettingName>Billing</SettingName>
                <SettingContent>
                  <preferencesForm.AppField name="preferences.paperlessBilling">
                    {field => (
                      <CheckboxGroup>
                        <CheckboxField>
                          <field.FormCheckbox />
                          <Label>Paperless Billing</Label>
                          <Description>Receive your bill by email.</Description>
                        </CheckboxField>
                      </CheckboxGroup>
                    )}
                  </preferencesForm.AppField>
                </SettingContent>
              </Setting>

              <Setting>
                <SettingName>Newsletter</SettingName>
                <SettingContent>
                  <preferencesForm.AppField name="preferences.paperNewsletterOptOut">
                    {field => (
                      <CheckboxGroup>
                        <CheckboxField>
                          <field.FormCheckbox />
                          <Label>Opt Out of the Paper Version of <em>The Current</em></Label>
                          <Description>One copy is mailed per membership — checking this will cancel paper delivery for your membership.</Description>
                        </CheckboxField>
                      </CheckboxGroup>
                    )}
                  </preferencesForm.AppField>
                </SettingContent>
              </Setting>
            </SettingsSet>
          </SettingsSection>
        </Settings>

        <ButtonGroup>
          <ButtonGroupSpacer/>
          <Button plain to="/settings/memberships">Cancel</Button>
          <preferencesForm.Subscribe selector={state => state.isSubmitting}>
            {isSubmitting => (
              <Button onClick={handleSavePreferencesClick} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            )}
          </preferencesForm.Subscribe>
        </ButtonGroup>
      </ContentSection>
    </main>
  );
}
